import {ArrowTopRightOnSquareIcon} from "@heroicons/react/20/solid";
import {useCallback, useEffect, useId, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {OtsActionButton, OtsActionLink} from "../components/OtsButton";
import {OtsGoBotBubble} from "../components/OtsGoBotBubble";
import {OtsGoBotLauncher} from "../components/OtsGoBotLauncher";
import {tooltipPortalTarget} from "../components/OtsTooltip";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";
import {browserFeedbackPromptMemory, FeedbackPromptMemory} from "./feedback-prompt-memory";
import {otsResourceUrls} from "./ots-support";

// DispatchGo/HubGo load Mantine's unlayered normalize CSS (`button {font-size: 100%}`, `a {background-color:
// transparent}`), which beats the Tailwind layer; important utilities keep the bubble's actions as designed.
const MANTINE_SAFE_TEXT = "!text-sm";

export type FeedbackPromptProps = {
  /** Where "Send feedback" goes; opens in a new tab. */
  feedbackUrl?: string;
  /**
   * `floating` pins GoBot to the bottom-right corner of the window; `inline` renders it where it is placed (a nav
   * bar, say) with the bubble opening below it. The inline bubble is portaled into GoBot's own root (the shadow root
   * in WaterGo) and fixed to the window, so a host panel that clips or stacks under its neighbors can't hide it.
   */
  placement?: "floating" | "inline";
  /** Opens the bubble by itself after sign-in, once per browser session, unless the person opted out. */
  autoOpen?: boolean;
  /** Wait before the automatic bubble, so it doesn't compete with the page loading. */
  autoOpenDelayMs?: number;
  /** Size of the round GoBot button in pixels. */
  launcherSize?: number;
  memory?: FeedbackPromptMemory;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  className?: string;
};

/**
 * GoBot asking for feedback: a round GoBot button that opens a bubble linking to the OTS feedback portal. Mount it
 * once inside the signed-in shell. It pops up by itself once per session, can always be closed, and stays out of the
 * way afterwards until someone clicks it.
 */
export const FeedbackPrompt = ({
  feedbackUrl = otsResourceUrls.feedback,
  placement = "floating",
  autoOpen = true,
  autoOpenDelayMs = 1500,
  launcherSize = 48,
  memory = browserFeedbackPromptMemory,
  brand,
  colorScheme,
  className,
}: FeedbackPromptProps) => {
  const [open, setOpen] = useState(false);
  // The bubble's GoBot says hello each time it opens; the button only on first appearance.
  const [greeting, setGreeting] = useState(0);
  const bubbleId = useId();
  const launcherRef = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<{top: number; right: number}>();

  const show = useCallback(() => {
    setGreeting((count) => count + 1);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!autoOpen || memory.optedOut() || memory.shownThisSession()) return;
    const timer = setTimeout(() => {
      memory.markShown();
      show();
    }, autoOpenDelayMs);
    return () => clearTimeout(timer);
  }, [autoOpen, autoOpenDelayMs, memory, show]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  const floating = placement === "floating";

  // Inline: keep the fixed bubble under GoBot as the window resizes or anything scrolls.
  useLayoutEffect(() => {
    if (floating || !open) return;
    const place = () => {
      const rect = launcherRef.current?.getBoundingClientRect();
      if (rect) setAnchor({top: rect.bottom + 12, right: Math.max(8, window.innerWidth - rect.right)});
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [floating, open]);

  const bubble = open && <div id={bubbleId}>
    <OtsGoBotBubble
      key={greeting}
      title="Hi, I'm GoBot. Got feedback?"
      goBotState="hello"
      tail={floating ? "bottom-end" : "top-end"}
      onClose={close}
      actions={<>
        <OtsActionButton
          variant="text"
          size="compact"
          className={MANTINE_SAFE_TEXT}
          onClick={() => {
            memory.optOut();
            close();
          }}>
          Don't ask at sign-in
        </OtsActionButton>
        <OtsActionLink
          variant="primary"
          size="compact"
          href={feedbackUrl}
          target="_blank"
          rel="noopener noreferrer"
          trailingIcon={<ArrowTopRightOnSquareIcon/>}
          className={`${MANTINE_SAFE_TEXT} !bg-[var(--ots-primary)]`}
          onClick={close}>
          Send feedback
        </OtsActionLink>
      </>}>
      Share an idea, report something that isn't working, or vote on what the OTS team builds next.
    </OtsGoBotBubble>
  </div>;

  return <OtsThemeRoot
    brand={brand}
    colorScheme={colorScheme}
    data-testid="feedback-prompt"
    className={[
      floating
        // Under the toast stack (z 1300), which shares this corner and is only there briefly.
        ? "pointer-events-none fixed bottom-4 right-4 z-[1200] flex flex-col items-end gap-3"
        : "relative inline-flex",
      className,
    ].filter(Boolean).join(" ")}>
    {floating
      ? <div aria-live="polite" className="pointer-events-auto">{bubble}</div>
      : bubble && anchor && createPortal(
        // Same layer as tooltips: above host shells (Mantine 100, the bar's menus 1001) and GWT panels.
        <OtsThemeRoot
          brand={brand}
          colorScheme={colorScheme}
          aria-live="polite"
          style={{top: anchor.top, right: anchor.right}}
          className="fixed z-[1300]">
          {bubble}
        </OtsThemeRoot>,
        tooltipPortalTarget(launcherRef.current))}
    <OtsGoBotLauncher
      ref={launcherRef}
      label="Send feedback to OTS"
      state="hello"
      size={launcherSize}
      expanded={open}
      aria-controls={open ? bubbleId : undefined}
      onClick={() => (open ? close() : show())}
      className={floating
        ? "pointer-events-auto"
        // In a bar it sits among icon buttons, so it drops the floating chrome and only highlights on hover.
        : "border-transparent bg-transparent shadow-none hover:scale-100 hover:bg-[var(--ots-neutral-container)]"}/>
  </OtsThemeRoot>;
};
